import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { formatApiError } from "../utils/formatApiError";
import { User, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { SKILL_LEVELS, SKILL_LEVEL_VALUES } from "../constants/skillLevels";
import RequirePhone from "../components/RequirePhone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // EventDetailPage forwards ?signup=play|class. Used purely for layout
  // (auto-focus on the class card when intent is "class"); the actual
  // signup endpoint and matching_type are decided server-side now.
  const intentSignupAction =
    searchParams.get("signup") === "class" ? "class"
    : searchParams.get("signup") === "play" ? "play"
    : null;
  const [signupType, setSignupType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [event, setEvent] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [isSoldOut, setIsSoldOut] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  useEffect(() => {
    const init = async () => {
      let token = localStorage.getItem("user_token");
      
      if (!token) {
        toast.error("Please login to sign up for events");
        navigate("/login", { state: { from: { pathname: `/events/${eventId}/signup` } } });
        return;
      }

      try {
        const [userRes, eventRes] = await Promise.all([
          api.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
          api.get(`${API}/events/${eventId}/players`)
        ]);
        setUser(userRes.data);

        // Check if profile is complete
        if (!userRes.data.is_profile_complete) {
          toast.error("Please complete your profile first");
          navigate("/complete-profile", { state: { from: { pathname: `/events/${eventId}/signup` } } });
          return;
        }
        
        const data = eventRes.data;
        setEventData(data);
        setEvent(data.event);
        
        // Calculate sold out status
        // For slotted events, total capacity = sum of slot max_players
        const eventSlots = data.event?.slots || [];
        const totalSlots = eventSlots.length > 0
          ? eventSlots.reduce((sum, s) => sum + (s.max_players || 0), 0)
          : (data.num_tables || 5) * 4;
        const fullTablePlayers = (data.full_bookings?.length || 0) * 4;
        const totalPlayers = (data.solo_players?.length || 0) + fullTablePlayers;
        const slotsLeft = Math.max(0, totalSlots - totalPlayers);
        
        if (slotsLeft === 0) {
          setIsSoldOut(true);
          toast.error("This event is sold out!");
        }
      } catch (error) {
        // 401 handled by global interceptor in api.js — clears token and redirects to /login
        if (error.response?.status === 404) {
          toast.error("Event not found");
          navigate("/");
        } else {
          toast.error("Failed to load event");
          navigate("/");
        }
      } finally {
        setCheckingAuth(false);
      }
    };

    init();
  }, [eventId, navigate]);

  // Solo Signup Data — matching_type collapsed to "auto" site-wide.
  // Admins handle all matching server-side; the unified flow no longer
  // surfaces auto/manual or full-table seating preferences.
  const [soloData, setSoloData] = useState({
    mahjong_style: "",
    skill_level: "",
    matching_type: "auto",
  });
  const [soloError, setSoloError] = useState("");

  // Class signup - selected teacher and style
  const [selectedClass, setSelectedClass] = useState(null); // {teacher_id, teacher_name, style}
  const [classSkillLevel, setClassSkillLevel] = useState("");
  const [hasPlayedBefore, setHasPlayedBefore] = useState(""); // For class signups: "yes", "no", ""
  const [classError, setClassError] = useState("");

  // Confirmation step between "style + skill selected" and "API call
  // fires" — shared across all three signup types. { type: "solo" |
  // "class" | "hosted", style, skillLevel } | null. Only the modal's
  // "Confirm & Submit" button calls the real submit handler now; the
  // handlers themselves are unchanged (still called directly by the
  // phone-gate retry flow, which shouldn't re-prompt for confirmation).
  const [confirmModal, setConfirmModal] = useState(null);

  // Mahjong style and skill level are never pre-filled from the user's
  // profile or a prior registration — every signup requires an explicit,
  // fresh selection. See handleSoloSubmit / handleHostedSignup /
  // handleClassSignup for the mandatory-selection checks.
  useEffect(() => {
    // Set default class selection
    if (event) {
      // Check for new multi-teacher format first
      if (event.event_teachers?.length > 0) {
        const firstTeacher = event.event_teachers[0];
        if (firstTeacher.styles_for_event?.length > 0) {
          setSelectedClass({
            teacher_id: firstTeacher.teacher_id,
            teacher_name: firstTeacher.teacher_name,
            style: firstTeacher.styles_for_event[0]
          });
        }
      } else if (event.class_styles?.length > 0 && event.class_teacher_name) {
        // Legacy single teacher format
        setSelectedClass({
          teacher_id: null,
          teacher_name: event.class_teacher_name,
          style: event.class_styles[0]
        });
      }
    }
  }, [user, event]);

  // Payment redirect state
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupResult, setSignupResult] = useState(null);

  // Server-side phone_required fallback: backend can reject a signup even
  // after the frontend gate if client state is stale. Capture the original
  // call as a retry closure, show the modal, replay after phone is saved.
  const [phoneFallback, setPhoneFallback] = useState(null); // { retry: () => Promise<void> } | null
  const isPhoneRequired = (err) =>
    err?.response?.status === 400 && err?.response?.headers?.["x-error-code"] === "phone_required";

  // Direct class signup - no form needed
  const handleClassSignup = async () => {
    if (!selectedClass) {
      setClassError("Please select a class option");
      return;
    }
    if (!classSkillLevel) {
      setClassError("Please select your skill level");
      return;
    }
    if (!hasPlayedBefore) {
      setClassError("Please indicate if you have played Mahjong before");
      return;
    }
    setClassError("");
    setLoading(true);
    try {
      const token = localStorage.getItem("user_token");
      await api.post(
        `${API}/events/${eventId}/signup/solo`,
        {
          mahjong_style: selectedClass.style,
          skill_level: classSkillLevel,
          matching_type: "class",
          teacher_id: selectedClass.teacher_id,
          teacher_name: selectedClass.teacher_name,
          has_played_before: hasPlayedBefore === "yes"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("You've joined the class! The organizer will assign you to a table.");
      navigate(`/events/${eventId}`);
    } catch (error) {
      if (isPhoneRequired(error)) {
        setUser(u => (u ? { ...u, phone: null } : u));
        setPhoneFallback({ retry: () => handleClassSignup() });
      } else {
        toast.error(formatApiError(error, "Failed to sign up for class"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSoloSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!soloData.mahjong_style || !soloData.skill_level) {
      setSoloError("Please select both a Mahjong style and a skill level");
      return;
    }
    setSoloError("");
    setLoading(true);
    const token = localStorage.getItem("user_token");

    try {
      const response = await api.post(`${API}/events/${eventId}/signup/solo`, soloData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (event?.payment_link && event?.cost_per_seat > 0 && event?.payment_method !== "venue") {
        setSignupResult({
          type: "solo",
          message: response.data.message,
          totalFee: event.cost_per_seat,
          paymentLink: event.payment_link
        });
        setSignupComplete(true);
      } else {
        toast.success(response.data.message);
        navigate(`/events/${eventId}`);
      }
    } catch (error) {
      if (isPhoneRequired(error)) {
        setUser(u => (u ? { ...u, phone: null } : u));
        setPhoneFallback({ retry: () => handleSoloSubmit() });
      } else {
        toast.error(formatApiError(error, "Failed to sign up"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Hosted event submit — no form event needed, no payment redirect (host handles it directly)
  const handleHostedSignup = async () => {
    if (!soloData.mahjong_style || !soloData.skill_level) {
      setSoloError("Please select both a Mahjong style and a skill level");
      return;
    }
    setSoloError("");
    setLoading(true);
    const token = localStorage.getItem("user_token");
    try {
      const payload = { ...soloData, matching_type: "auto" };
      if (selectedSlotId) payload.slot_id = selectedSlotId;
      const response = await api.post(
        `${API}/events/${eventId}/signup/solo`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "You're signed up!");
      navigate(`/events/${eventId}`);
    } catch (error) {
      if (isPhoneRequired(error)) {
        setUser(u => (u ? { ...u, phone: null } : u));
        setPhoneFallback({ retry: () => handleHostedSignup() });
      } else {
        toast.error(formatApiError(error, "Failed to sign up"));
      }
    } finally {
      setLoading(false);
    }
  };

  // Confirmation-step openers — validate exactly as the submit handlers
  // above do, then show the confirm modal instead of calling the API
  // directly. The modal's "Confirm & Submit" button is now the only path
  // that actually fires the signup request for these three flows.
  const openSoloConfirm = () => {
    if (!soloData.mahjong_style || !soloData.skill_level) {
      setSoloError("Please select both a Mahjong style and a skill level");
      return;
    }
    setSoloError("");
    setConfirmModal({ type: "solo", style: soloData.mahjong_style, skillLevel: soloData.skill_level });
  };

  const openClassConfirm = () => {
    if (!selectedClass) {
      setClassError("Please select a class option");
      return;
    }
    if (!classSkillLevel) {
      setClassError("Please select your skill level");
      return;
    }
    if (!hasPlayedBefore) {
      setClassError("Please indicate if you have played Mahjong before");
      return;
    }
    setClassError("");
    setConfirmModal({ type: "class", style: selectedClass.style, skillLevel: classSkillLevel });
  };

  const openHostedConfirm = () => {
    if (!soloData.mahjong_style || !soloData.skill_level) {
      setSoloError("Please select both a Mahjong style and a skill level");
      return;
    }
    setSoloError("");
    setConfirmModal({ type: "hosted", style: soloData.mahjong_style, skillLevel: soloData.skill_level });
  };

  const handleConfirmModalSubmit = async () => {
    if (!confirmModal) return;
    const { type } = confirmModal;
    if (type === "solo") await handleSoloSubmit();
    else if (type === "class") await handleClassSignup();
    else if (type === "hosted") await handleHostedSignup();
    setConfirmModal(null);
  };

  const confirmModalElement = confirmModal && (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem"
    }}>
      <div style={{
        background: "white", borderRadius: "12px", padding: "1.5rem",
        maxWidth: "420px", width: "100%", maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
      }}>
        <h3 style={{
          color: "#D32F2F", margin: "0 0 0.5rem", fontSize: "1.4rem", fontWeight: "800",
          textTransform: "uppercase", letterSpacing: "0.5px"
        }}>
          Confirm your signup
        </h3>
        <p style={{ color: "#666660", fontSize: "0.9rem", marginBottom: "1rem" }}>
          {event?.event_name || event?.venue_name}
        </p>
        <div style={{ background: "#F5F7F5", borderRadius: "8px", padding: "0.9rem 1rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.6rem" }}>
            <span style={{ color: "#666", fontSize: "0.85rem" }}>Style</span>
            <strong style={{ color: "#D32F2F", fontSize: "0.9rem" }}>{confirmModal.style}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#666", fontSize: "0.85rem" }}>Skill Level</span>
            <strong style={{ color: "#D32F2F", fontSize: "0.9rem" }}>{confirmModal.skillLevel}</strong>
          </div>
        </div>

        <button
          onClick={() => setConfirmModal(null)}
          className="btn btn-secondary"
          style={{ width: "100%" }}
          disabled={loading}
          data-testid="confirm-signup-back"
        >
          Go back and edit
        </button>

        <button
          onClick={handleConfirmModalSubmit}
          className="btn btn-primary"
          style={{ width: "100%", marginTop: "0.6rem" }}
          disabled={loading}
          data-testid="confirm-signup-submit"
        >
          {loading ? "Submitting..." : "Confirm & Submit"}
        </button>

        <p style={{ fontSize: "0.75rem", color: "#888", textAlign: "center", lineHeight: "1.5", margin: "0.75rem 0 0" }}>
          Please double-check your selections above — once submitted, we won't be able to change your style or skill level. This helps us keep table assignments smooth for everyone.
        </p>
      </div>
    </div>
  );

  const isHostedEvent = event?.event_type === "guest_host";

  const seoTitle = event?.venue_name
    ? `Sign Up for ${event.event_name || event.venue_name}`
    : "Loading...";
  const seoDesc = event?.venue_name && event?.date
    ? `Sign up to play Mahjong at ${event.event_name || event.venue_name} on ${event.date}.`
    : "";
  const seoElement = (
    <SEO
      title={seoTitle}
      description={seoDesc}
      path={`/events/${eventId}/signup`}
      noindex
    />
  );

  if (checkingAuth) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {seoElement}
        <Header />
        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <div className="loading">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  // Payment prompt after signup
  if (signupComplete && signupResult) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header pageTitle="Complete Your Payment" />

        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <div className="card" style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center" }}>
            <div style={{ fontSize: "4rem", marginBottom: "1rem", color: "#2E7D32" }}>✓</div>
            <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#1B4D3E" }}>
              {signupResult.type === "full_table" 
                ? `Table ${signupResult.tableNumber} Reserved!`
                : "You're Signed Up!"}
            </h2>
            
            <p style={{ color: "#666660", marginBottom: "1.5rem" }}>
              {signupResult.type === "full_table"
                ? "Your table is reserved. Please complete payment to confirm your booking."
                : signupResult.message}
            </p>

            <div style={{ 
              padding: "1.5rem", 
              background: "#FFF3E0", 
              borderRadius: "8px", 
              marginBottom: "1.5rem",
              border: "2px solid #FFE0B2"
            }}>
              <p style={{ fontSize: "1.25rem", fontWeight: "700", color: "#E65100", marginBottom: "0.5rem" }}>
                Amount Due: ₹{signupResult.totalFee}
              </p>
              <p style={{ fontSize: "0.875rem", color: "#666660" }}>
                {signupResult.type === "full_table" ? "(4 seats)" : "(1 seat)"}
              </p>
            </div>

            <a
              href={signupResult.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ 
                width: "100%", 
                fontSize: "1.125rem",
                padding: "1rem",
                textDecoration: "none",
                display: "block",
                marginBottom: "1rem"
              }}
              data-testid="pay-now-button"
            >
              Pay Now
            </a>

            <Link
              to={`/events/${eventId}`}
              className="btn btn-secondary"
              style={{ width: "100%" }}
            >
              I'll Pay Later
            </Link>

            <p style={{ fontSize: "0.75rem", color: "#999", marginTop: "1rem" }}>
              Your spot is held but not confirmed until payment is received.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Phone gate: if the user is loaded but has no phone on file, prompt for it
  // before any signup UI renders. Also triggered by the phone_required server
  // fallback: catch blocks clear user.phone + set phoneFallback.retry, and
  // onPhoneSaved below replays the retry once a phone is saved.
  if (user && !user.phone) {
    return (
      <RequirePhone
        variant="page"
        user={user}
        heading="Add Your Phone Number"
        onPhoneSaved={async (updated) => {
          setUser(updated);
          if (phoneFallback) {
            const retry = phoneFallback.retry;
            setPhoneFallback(null);
            try { await retry(); } catch (_) { /* handler owns its error state */ }
          }
        }}
      />
    );
  }

  // Step 1: Choose signup type
  if (!signupType) {
    // Check if sold out
    if (isSoldOut) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          <Header pageTitle="Event Sold Out" />

          <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
            <div className="card" style={{ maxWidth: "500px", margin: "0 auto", textAlign: "center", padding: "2rem" }}>
              <div style={{ 
                fontSize: "3rem", 
                marginBottom: "1rem",
                background: "linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: "700"
              }}>
                SOLD OUT
              </div>
              <h2 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", color: "#333" }}>
                {event?.event_name || event?.venue_name}
              </h2>
              <p style={{ color: "#666660", marginBottom: "1.25rem", lineHeight: "1.5", fontSize: "0.9rem" }}>
                Sorry, all spots have been filled. Check back later - spots may open up if someone drops out!
              </p>
              <Link to="/" className="btn btn-primary" style={{ width: "100%" }}>
                Browse Other Events
              </Link>
            </div>
          </div>
          <Footer />
        </div>
      );
    }
    
    // Hosted event: show style + skill confirmation form (no solo/full table choice)
    if (isHostedEvent) {
      const availableStyles = event?.styles_of_play || [];
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
          <Header pageTitle="Sign Up for Event" />
          <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
            <div className="card" style={{ maxWidth: "500px" }}>
              <div style={{
                padding: "0.75rem",
                background: "#F5F7F5",
                borderRadius: "8px",
                marginBottom: "1.25rem",
                textAlign: "center",
              }}>
                <p style={{ color: "#666660", margin: 0, fontSize: "0.85rem" }}>
                  Signing up as: <strong style={{ color: "#1B4D3E" }}>{user?.name || user?.email}</strong>
                </p>
              </div>

              <div style={{
                padding: "1rem",
                border: "1px solid #E2E2DC",
                borderRadius: "10px",
                background: "#FAFAFA",
                marginBottom: "1.25rem",
              }}>
                <h3 style={{ marginBottom: "1rem", color: "#1B4D3E", fontSize: "1rem", fontWeight: "600" }}>
                  Confirm your details
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#444", marginBottom: "0.5rem" }}>
                      Mahjong Style
                    </label>
                    <select
                      className="input"
                      style={{ cursor: "pointer" }}
                      value={soloData.mahjong_style}
                      onChange={(e) => {
                        // Style change clears skill_level — never re-derived
                        // from the profile, always picked fresh.
                        setSoloData({ ...soloData, mahjong_style: e.target.value, skill_level: "" });
                      }}
                    >
                      <option value="">-- Select a style --</option>
                      {availableStyles.map((style) => (
                        <option key={style} value={style}>{style}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#444", marginBottom: "0.5rem" }}>
                      Skill Level
                    </label>
                    <select
                      className="input"
                      style={{ cursor: "pointer" }}
                      value={soloData.skill_level}
                      onChange={(e) => setSoloData({ ...soloData, skill_level: e.target.value })}
                    >
                      <option value="">-- Select your skill level --</option>
                      {SKILL_LEVEL_VALUES.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {soloError && (
                <p style={{ color: "#D32F2F", fontSize: "0.85rem", marginTop: "-0.5rem", marginBottom: "0.75rem" }}>{soloError}</p>
              )}

              <button
                onClick={() => {
                  if (!soloData.mahjong_style || !soloData.skill_level) {
                    setSoloError("Please select both a Mahjong style and a skill level");
                    return;
                  }
                  setSoloError("");
                  const slots = event?.slots || [];
                  setSignupType(slots.length > 0 ? "hosted_slot_select" : "hosted_disclaimer");
                }}
                className="btn btn-primary"
                style={{ width: "100%" }}
              >
                Continue →
              </button>
            </div>
          </div>
          <Footer />
        </div>
      );
    }

    // Decide which signup cards to render given the URL intent and the
    // event's classes_available flag. When only one card will render, the
    // grid stretches a single 280px-min card across the row, which looks
    // wide on desktop. Switch to a single centred column at a tighter
    // max-width in that case.
    const showSoloCard = intentSignupAction !== "class";
    const showClassCard = !!event?.classes_available && intentSignupAction !== "play";
    const singleCard = (showSoloCard && !showClassCard) || (!showSoloCard && showClassCard);
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {seoElement}
        <Header pageTitle="Sign Up for Event" />
        {confirmModalElement}

        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <div style={{
            display: "grid",
            gap: "1rem",
            gridTemplateColumns: singleCard ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
            maxWidth: singleCard ? "480px" : "700px",
            margin: singleCard ? "0 auto" : undefined,
          }}>
            {/* Solo Signup Card — hidden when the user came from the
                "Sign up for a class" CTA on EventDetailPage. */}
            {showSoloCard && (
            <div className="card" style={{ padding: "1.5rem" }} data-testid="solo-option">
              <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                <User size={40} color="#1B4D3E" style={{ marginBottom: "0.5rem" }} />
                <h2 style={{ fontSize: "1.2rem", marginBottom: "0", color: "#1B4D3E", fontWeight: "700" }}>Sign up to play</h2>
              </div>

              {/* Style selector */}
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#444", marginBottom: "0.5rem" }}>
                  Mahjong Style
                </label>
                <select
                  className="input"
                  style={{ cursor: "pointer" }}
                  value={soloData.mahjong_style}
                  onChange={(e) => {
                    // Style change clears skill_level — never re-derived
                    // from the profile, always picked fresh.
                    setSoloData({
                      ...soloData,
                      mahjong_style: e.target.value,
                      skill_level: ""
                    });
                  }}
                  data-testid="solo-style"
                >
                  <option value="">-- Select a style --</option>
                  {event?.allowed_styles?.map((style) => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
              </div>

              {/* Skill level — required, user-selectable, always starts
                  blank. Never pre-filled from a profile or prior signup. */}
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#444", marginBottom: "0.5rem" }}>
                  Skill Level
                </label>
                <select
                  className="input"
                  style={{ cursor: "pointer" }}
                  value={soloData.skill_level}
                  onChange={(e) => setSoloData({ ...soloData, skill_level: e.target.value })}
                  required
                  data-testid="solo-skill"
                >
                  <option value="">-- Select your skill level --</option>
                  {SKILL_LEVELS.map((sl) => (
                    <option key={sl.value} value={sl.value}>
                      {sl.label} — {sl.description}
                    </option>
                  ))}
                </select>
              </div>

              {soloError && (
                <p style={{ color: "#D32F2F", fontSize: "0.85rem", marginTop: "-0.5rem", marginBottom: "0.75rem" }}>{soloError}</p>
              )}

              <button
                onClick={openSoloConfirm}
                className="btn btn-primary"
                style={{ width: "100%" }}
                disabled={loading}
                data-testid="submit-solo"
              >
                {loading ? "Signing Up..." : "Sign up to play"}
              </button>
            </div>
            )}

            {/* Join a Class Option - Only show if classes are available */}
            {showClassCard && (
              <div className="card" style={{ padding: "1.5rem" }} data-testid="class-option">
                <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                  <GraduationCap size={40} color="#1B4D3E" style={{ marginBottom: "0.5rem" }} />
                  <h2 style={{ fontSize: "1.2rem", marginBottom: "0", color: "#1B4D3E", fontWeight: "700" }}>Sign up for a class</h2>
                </div>
                
                {/* Multiple Teachers - New Format */}
                {event.event_teachers?.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {event.event_teachers.map((teacher, idx) => (
                      <div 
                        key={teacher.teacher_id || idx}
                        style={{ 
                          padding: "1rem", 
                          background: "#F9F7F1", 
                          borderRadius: "8px",
                          borderLeft: "4px solid #9C27B0"
                        }}
                      >
                        <h3 style={{ color: "#9C27B0", fontSize: "1rem", marginBottom: "0.5rem" }}>
                          {teacher.teacher_name}
                        </h3>
                        {teacher.teacher_phone && (
                          <p style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                            <strong>Contact:</strong>{" "}
                            <a href={`tel:${teacher.teacher_phone}`} style={{ color: "#9C27B0" }}>
                              {teacher.teacher_phone}
                            </a>
                          </p>
                        )}
                        {teacher.styles_for_event?.length > 0 && (
                          <p style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                            <strong>Teaching:</strong> {teacher.styles_for_event.join(", ")}
                          </p>
                        )}
                        {teacher.fee > 0 && (
                          <p style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}>
                            <strong>Fee:</strong> ₹{teacher.fee} per class
                          </p>
                        )}
                        <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                          <strong>Minimums:</strong>{" "}
                          {teacher.min_signups > 0 
                            ? `Requires minimum ${teacher.min_signups} class${teacher.min_signups > 1 ? 'es' : ''}.`
                            : "No minimum required."
                          }
                        </p>
                      </div>
                    ))}
                    
                    {/* Class Selection Dropdown - only show if multiple options */}
                    {event.event_teachers.flatMap(t => t.styles_for_event || []).length > 1 && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", color: "#7B1FA2", marginBottom: "0.5rem" }}>
                        Select your class:
                      </label>
                      <select
                        className="input"
                        value={selectedClass ? `${selectedClass.teacher_id}|${selectedClass.style}` : ""}
                        onChange={(e) => {
                          const [teacherId, style] = e.target.value.split("|");
                          const teacher = event.event_teachers.find(t => t.teacher_id === teacherId);
                          setSelectedClass({
                            teacher_id: teacherId,
                            teacher_name: teacher?.teacher_name || "",
                            style: style
                          });
                        }}
                        style={{ cursor: "pointer", borderColor: "#9C27B0" }}
                        data-testid="class-select"
                      >
                        <option value="">-- Select a class --</option>
                        {event.event_teachers.flatMap(teacher => 
                          (teacher.styles_for_event || []).map(style => (
                            <option 
                              key={`${teacher.teacher_id}-${style}`} 
                              value={`${teacher.teacher_id}|${style}`}
                            >
                              {teacher.teacher_name} - {style}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    )}
                  </div>
                ) : (
                  /* Legacy Single Teacher Format */
                  <div style={{ textAlign: "left", fontSize: "0.9rem", lineHeight: "1.8" }}>
                    {event.class_teacher_name && (
                      <p style={{ marginBottom: "0.25rem" }}>
                        <strong>Teacher:</strong> {event.class_teacher_name}
                      </p>
                    )}
                    {event.class_teacher_phone && (
                      <p style={{ marginBottom: "0.25rem" }}>
                        <strong>Contact:</strong> <a href={`tel:${event.class_teacher_phone}`} style={{ color: "#9C27B0" }}>{event.class_teacher_phone}</a>
                      </p>
                    )}
                    {event.class_styles?.length > 0 && (
                      <p style={{ marginBottom: "0.25rem" }}>
                        <strong>Styles taught:</strong> {event.class_styles.join(", ")}
                      </p>
                    )}
                    {event.class_fee > 0 && (
                      <p style={{ marginBottom: "0.25rem" }}>
                        <strong>Class Fee:</strong> ₹{event.class_fee} per class
                      </p>
                    )}
                    <p style={{ marginBottom: "0.25rem" }}>
                      <strong>Minimums:</strong>{" "}
                      {event.class_min_signups > 0 
                        ? `This teacher requires you to sign up for a minimum of ${event.class_min_signups} classes.`
                        : "This teacher does not require any minimum number of classes."
                      }
                    </p>
                    
                    {/* Style Selection - only show if multiple styles available */}
                    {event.class_styles?.length > 1 && (
                      <div style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
                        <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", color: "#7B1FA2", marginBottom: "0.5rem" }}>
                          Which style would you like to learn?
                        </label>
                        <select
                          className="input"
                          value={selectedClass?.style || ""}
                          onChange={(e) => setSelectedClass({
                            teacher_id: null,
                            teacher_name: event.class_teacher_name,
                            style: e.target.value
                          })}
                          style={{ cursor: "pointer", borderColor: "#9C27B0" }}
                          data-testid="class-style-select"
                        >
                          {event.class_styles.map((style) => (
                            <option key={style} value={style}>{style}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Have you played Mahjong before? */}
                <div style={{ marginTop: "1rem" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", color: "#7B1FA2", marginBottom: "0.5rem" }}>
                    Have you played Mahjong before? *
                  </label>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <label style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "0.5rem", 
                      cursor: "pointer",
                      padding: "0.5rem 1rem",
                      background: hasPlayedBefore === "yes" ? "#F3E5F5" : "#fff",
                      border: hasPlayedBefore === "yes" ? "2px solid #9C27B0" : "1px solid #ddd",
                      borderRadius: "6px"
                    }}>
                      <input
                        type="radio"
                        name="hasPlayedBefore"
                        value="yes"
                        checked={hasPlayedBefore === "yes"}
                        onChange={() => setHasPlayedBefore("yes")}
                        data-testid="played-before-yes"
                      />
                      <span>Yes</span>
                    </label>
                    <label style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "0.5rem", 
                      cursor: "pointer",
                      padding: "0.5rem 1rem",
                      background: hasPlayedBefore === "no" ? "#F3E5F5" : "#fff",
                      border: hasPlayedBefore === "no" ? "2px solid #9C27B0" : "1px solid #ddd",
                      borderRadius: "6px"
                    }}>
                      <input
                        type="radio"
                        name="hasPlayedBefore"
                        value="no"
                        checked={hasPlayedBefore === "no"}
                        onChange={() => setHasPlayedBefore("no")}
                        data-testid="played-before-no"
                      />
                      <span>No, I'm new!</span>
                    </label>
                  </div>
                </div>

                {/* Skill level — required, user-selectable, always starts
                    blank. Never pre-filled from a profile or prior signup. */}
                <div style={{ marginTop: "1rem" }}>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", color: "#7B1FA2", marginBottom: "0.5rem" }}>
                    Skill Level *
                  </label>
                  <select
                    className="input"
                    style={{ cursor: "pointer", borderColor: "#9C27B0" }}
                    value={classSkillLevel}
                    onChange={(e) => setClassSkillLevel(e.target.value)}
                    data-testid="class-skill"
                  >
                    <option value="">-- Select your skill level --</option>
                    {SKILL_LEVELS.map((sl) => (
                      <option key={sl.value} value={sl.value}>
                        {sl.label} — {sl.description}
                      </option>
                    ))}
                  </select>
                </div>

                <p style={{ color: "#666", fontSize: "0.75rem", marginTop: "1rem", marginBottom: "1rem", background: "#F3E5F5", padding: "0.5rem", borderRadius: "6px", textAlign: "center" }}>
                  All class queries and payments to be coordinated directly with the teacher.
                </p>
                {classError && (
                  <p style={{ color: "#D32F2F", fontSize: "0.85rem", marginTop: "-0.5rem", marginBottom: "0.75rem" }}>{classError}</p>
                )}
                <button
                  onClick={openClassConfirm}
                  className="btn"
                  style={{ width: "100%", background: "#9C27B0", color: "#fff" }}
                  disabled={loading}
                  data-testid="join-class-btn"
                >
                  {loading ? "Joining..." : "Join a Class"}
                </button>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 2a: Slot selection for hosted events with slots
  if (signupType === "hosted_slot_select") {
    const slots = event?.slots || [];
    const soloPlayers = eventData?.solo_players || [];
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header pageTitle="Select a Time Slot" />
        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <button
            onClick={() => { setSignupType(null); setSelectedSlotId(null); }}
            className="btn btn-secondary"
            style={{ marginBottom: "1rem" }}
          >
            ← Back
          </button>
          <div className="card" style={{ maxWidth: "500px" }}>
            <h2 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1B4D3E", marginBottom: "0.5rem" }}>
              Choose your time slot
            </h2>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "1.25rem" }}>
              Select when you'd like to play
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.25rem" }}>
              {slots.map((slot) => {
                const slotSignups = soloPlayers.filter(p => p.slot_id === slot.slot_id).length;
                const spotsLeft = Math.max(0, slot.max_players - slotSignups);
                const isFull = spotsLeft === 0;
                const isSelected = selectedSlotId === slot.slot_id;
                return (
                  <button
                    key={slot.slot_id}
                    type="button"
                    disabled={isFull}
                    onClick={() => setSelectedSlotId(slot.slot_id)}
                    style={{
                      padding: "1rem",
                      borderRadius: "10px",
                      border: isSelected ? "2px solid #1B4D3E" : "1px solid #E0E0E0",
                      background: isFull ? "#F5F5F5" : isSelected ? "#E8F5E9" : "#FFF",
                      cursor: isFull ? "not-allowed" : "pointer",
                      opacity: isFull ? 0.6 : 1,
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontWeight: "600", fontSize: "0.95rem", color: isFull ? "#999" : "#1B1B1B", marginBottom: "0.25rem" }}>
                      {slot.start_time} – {slot.end_time}
                    </div>
                    <span style={{
                      fontSize: "0.8rem",
                      padding: "0.15rem 0.5rem",
                      borderRadius: "20px",
                      background: isFull ? "#FFEBEE" : "#E8F5E9",
                      color: isFull ? "#C62828" : "#2E7D32",
                      fontWeight: "600"
                    }}>
                      {isFull ? "Full" : `${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left`}
                    </span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setSignupType("hosted_disclaimer")}
              className="btn btn-primary"
              style={{ width: "100%" }}
              disabled={!selectedSlotId}
            >
              Continue →
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Step 2b: Disclaimer for hosted events
  if (signupType === "hosted_disclaimer") {
    const hostName = event?.host_display_name || "the host";
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header pageTitle="Before You Sign Up" />
        {confirmModalElement}
        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <button
            onClick={() => {
              const slots = event?.slots || [];
              setSignupType(slots.length > 0 ? "hosted_slot_select" : null);
            }}
            className="btn btn-secondary"
            style={{ marginBottom: "1rem" }}
          >
            ← Back
          </button>
          <div className="card" style={{ maxWidth: "500px" }}>
            <h2 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#1B4D3E", marginBottom: "1.25rem" }}>
              Before you sign up
            </h2>
            <div style={{
              padding: "1.25rem",
              background: "#F8F8F6",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              lineHeight: "1.7",
              fontSize: "0.9rem",
              color: "#333",
            }}>
              <p style={{ marginBottom: "1rem" }}>
                This event is organised and managed by <strong>{hostName}</strong>.
              </p>
              <p style={{ marginBottom: "1rem" }}>
                Table assignments and payments are handled exclusively by the host. If you have any questions about the event, they're your point of contact.
              </p>
              <p style={{ marginBottom: "1rem" }}>
                By signing up you confirm you understand this.
              </p>
              <p style={{ margin: 0, fontWeight: "600", color: "#1B4D3E" }}>
                Happy playing!
              </p>
            </div>
            <button
              onClick={openHostedConfirm}
              className="btn btn-primary"
              style={{ width: "100%" }}
              disabled={loading}
            >
              {loading ? "Signing up..." : "I understand, sign me up"}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Solo signup is now handled inline on the type-selection screen — no separate form needed.
  return null;
};

export default SignupPage;
